/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import { Redirect } from 'react-router';

import { Query } from '@apollo/client/react/components';
import { Loader } from '@opendoor/bricks/core';
import ContentWrapper from '@opendoor/bricks/deprecated/UNSAFE_layout/ContentWrapper';
import IconNavBar, { IIconNavBarItem } from '@opendoor/bricks/deprecated/UNSAFE_layout/IconNavBar';
import _ from 'lodash';

import {
  ALL,
  DropdownMultiSelect,
  DropdownSelect,
  OptionType,
} from '../../components/DropdownSelects';
import FullPageHomeLoader from '../../components/FullPageHomeLoader';
import ExitLinkIcon from '../../components/icons/ExitLinkIcon';
import Layout from '../../components/Layout';
import PartnerSpecificHomeLink from '../../components/PartnerSpecificHomeLink';
import Report from '../../components/Report';
import ScrollAwareNavigationBox from '../../components/ScrollAwareNavigationBox';
import { Tab } from '../../components/Tab';
import { DATE_OPTIONS, MARKET_OPTIONS, reportConfigMap, ReportDisplay } from '../../config/reports';
import {
  GET_REPORTS,
  IGetReports,
  IGetReportsVariables,
  IReportData,
} from '../../graphql/queries/GetReports';
import { datesToParams, optionToDates } from '../../helpers/formatUtils';
import { FilterBar, FilterTitle } from '../../styles/components';
import styles from '../../styles/ReportingDashboardStyle.less';
import { LoadStateLabel } from '../../types';
import { PrivateRouteProps } from '../PrivateRoute';
import { ERROR_PAGE_ROUTE } from '../routes';

export const REPORTING_DASHBOARD_NAME = 'reporting dashboard';

export const marketSelectionSummary = (values: Array<OptionType> | undefined) => {
  if (!values) {
    return 'none';
  }
  const valueValues = values.map((v) => v.value);
  if (valueValues.indexOf(ALL.value) !== -1) {
    return 'All Markets';
  }

  return valueValues.length + ' Markets';
};

type ReportingDashboardState = {
  markets: Array<string>;
  dateFrame: string;
} & { [K in LoadStateLabel]: boolean };

type ReportQueryOutput =
  | { data?: IGetReports; loading: true }
  | { data: IGetReports; loading: false };

export class LegacyReportingDashboard extends React.PureComponent<
  unknown,
  ReportingDashboardState
> {
  public static isAuthorized: PrivateRouteProps['isAuthorized'] = (human) =>
    human.has_partnership_reporting_dashboard;

  public static dashboardName: string = REPORTING_DASHBOARD_NAME;

  public state: ReportingDashboardState = {
    chartsLoaded: false,
    dateFrame: DATE_OPTIONS[0].value,
    markets: MARKET_OPTIONS.map((d) => d.value),
    salesLoaded: false,
    summaryLoaded: false,
    customersLoaded: false,
    customersFullLoaded: true,
    salesFullLoaded: true,
    addressLoaded: false,
  };

  public render() {
    const { markets, dateFrame } = this.state;
    const { startDate, endDate } = datesToParams(optionToDates(dateFrame));

    return (
      <Query<IGetReports, IGetReportsVariables>
        query={GET_REPORTS}
        variables={{ marketNames: markets, startDate, endDate }}
        fetchPolicy="no-cache"
      >
        {({ loading, error, data }) => {
          if (error) {
            return <Redirect to={ERROR_PAGE_ROUTE} />;
          }

          const reportQueryOutput = {
            data,
            loading,
          } as ReportQueryOutput;

          return (
            <Layout
              renderContent={this.renderContent(reportQueryOutput)}
              renderNav={this.renderNav(reportQueryOutput)}
            />
          );
        }}
      </Query>
    );
  }

  public onReportLoadStateChange = (loadStateLabel: LoadStateLabel, isLoading: boolean) => {
    this.setState({ [loadStateLabel]: isLoading } as Pick<ReportingDashboardState, LoadStateLabel>);
  };

  public updateMarket = (values: Array<OptionType> | undefined) => {
    if (values && values.length) {
      const markets = values.filter((v) => v.value !== ALL.value).map((v) => v.value);
      this.setState({
        markets,
        summaryLoaded: false,
        chartsLoaded: false,
        salesLoaded: false,
        customersLoaded: false,
      });
    }
  };

  public updateDateFrame = (value: OptionType | undefined) => {
    if (value && value.value !== this.state.dateFrame) {
      this.setState({
        dateFrame: value.value,
        summaryLoaded: false,
        salesLoaded: false,
        customersLoaded: false,
      });
    }
  };

  public renderNav = (queryOutput: ReportQueryOutput) => {
    if (queryOutput.loading) {
      // eslint-disable-next-line react/display-name
      return () => <Loader />;
    }

    const reports = queryOutput.data.reports;
    // eslint-disable-next-line react/display-name
    return () => (
      <div className={styles['nav-container']}>
        <ScrollAwareNavigationBox
          algorithm="elementMappedToScrollPosition"
          links={reports
            .filter((r) => reportConfigMap.get(r.reportId)!.displayType !== ReportDisplay.HIDDEN)
            .map((report) => ({
              idToWatch: reportConfigMap.get(report.reportId)!.sectionId,
              text: reportConfigMap.get(report.reportId)!.iconText,
            }))}
          render={this.renderIconNav(reports)}
        />
        <div className={`${styles['nav-divider']} large-margin-top medium-margin-bottom`} />
        <PartnerSpecificHomeLink className={styles['home-link']}>
          Opendoor.com
          <span className={styles['home-link-icon']}>
            <ExitLinkIcon />
          </span>
        </PartnerSpecificHomeLink>
      </div>
    );
  };

  public renderIconNav = (reports: Array<IReportData>) => {
    const navIcons = reports
      .filter((r) => reportConfigMap.get(r.reportId)!.displayType !== ReportDisplay.HIDDEN)
      .map((report) => reportConfigMap.get(report.reportId)!.navIcon);

    const renderIconNavHelper: ScrollAwareNavigationBox['props']['render'] = (_, _2, links) => (
      <IconNavBar
        navBarItems={links.map((link, index) => ({
          icon: navIcons[index],
          isActive: link.active,
          link: link.href,
          name: link.text,
          render: this.renderTab,
        }))}
      />
    );

    return renderIconNavHelper;
  };

  private renderTab = (item: IIconNavBarItem, content: JSX.Element, key: string) => (
    <Tab item={item} content={content} key={key} />
  );

  public getReportElements = (queryOutput: ReportQueryOutput) => {
    if (queryOutput.loading) {
      return <FullPageHomeLoader />;
    }

    const reports = queryOutput.data.reports;
    const waitingForMode = !reports
      .map((report) => reportConfigMap.get(report.reportId)!)
      .every((type) => this.state[type.loadStateLabel]);

    const reportMap = _.groupBy(reports, (r) => r.reportId);
    const reportElements = reports
      .filter((r) => reportConfigMap.get(r.reportId)!.displayType !== ReportDisplay.HIDDEN)
      .map((r) => (
        <Report
          key={reportConfigMap.get(r.reportId)!.sectionId}
          btnText={reportConfigMap.get(r.reportId)!.iconText}
          displayType={reportConfigMap.get(r.reportId)!.displayType}
          id={reportConfigMap.get(r.reportId)!.sectionId}
          reportId={r.reportId}
          loading={waitingForMode}
          loadStateLabel={reportConfigMap.get(r.reportId)!.loadStateLabel}
          onLoadStateChange={this.onReportLoadStateChange}
          url={r.url}
          hasFullReport={!!reportMap[r.reportId + '-full']}
        />
      ));

    return (
      <>
        {waitingForMode && <FullPageHomeLoader />}
        {reportElements}
      </>
    );
  };

  public renderContent = (reportContent: ReportQueryOutput) => {
    // eslint-disable-next-line react/display-name
    return () => (
      <ContentWrapper>
        <FilterBar>
          <FilterTitle>Filter:</FilterTitle>
          <DropdownMultiSelect
            aria-labelledby="market"
            options={MARKET_OPTIONS}
            includeAllOption={true}
            defaultValues={MARKET_OPTIONS}
            onChange={this.updateMarket}
            selectionSummary={marketSelectionSummary}
          />
          <DropdownSelect
            options={DATE_OPTIONS}
            defaultValue={DATE_OPTIONS[0]}
            onChange={this.updateDateFrame}
          />
        </FilterBar>
        {this.getReportElements(reportContent)}
      </ContentWrapper>
    );
  };
}
